/**
 * InstallAppModal - PWA Installation prompt modal
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Download, X, Smartphone, Monitor, Check, Share, MoreVertical } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { trackPwa } from '../utils/trackPwa';

const InstallAppModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installed, setInstalled] = useState(false);

  const txt = {
    es: {
      title: 'Instalar DamyxIA',
      subtitle: 'Accede mas rapido desde tu dispositivo',
      alreadyInstalled: 'App ya instalada',
      alreadyInstalledDesc: 'DamyxIA ya esta instalada en tu dispositivo. Puedes acceder desde tu pantalla de inicio.',
      installBtn: 'Instalar App',
      installing: 'Instalando...',
      benefits: 'Beneficios de instalar:',
      benefit1: 'Acceso rapido desde tu pantalla de inicio',
      benefit2: 'Experiencia a pantalla completa',
      benefit3: 'Funciona sin conexion (parcial)',
      benefit4: 'Notificaciones push',
      iosTitle: 'Instalar en iOS',
      iosStep1: 'Toca el boton Compartir',
      iosStep2: 'Selecciona "Agregar a pantalla de inicio"',
      iosStep3: 'Toca "Agregar" para confirmar',
      androidTitle: 'Instalar en Android',
      androidStep1: 'Toca el menu (3 puntos)',
      androidStep2: 'Selecciona "Instalar aplicacion"',
      androidStep3: 'Confirma la instalacion',
      desktopTitle: 'Instalar en Escritorio',
      desktopStep1: 'Haz clic en el icono de instalacion en la barra de direcciones',
      desktopStep2: 'O usa el boton de abajo',
      installed: 'App instalada correctamente!',
      installError: 'No se pudo instalar la app',
    },
    en: {
      title: 'Install DamyxIA',
      subtitle: 'Access faster from your device',
      alreadyInstalled: 'App already installed',
      alreadyInstalledDesc: 'DamyxIA is already installed on your device. You can access it from your home screen.',
      installBtn: 'Install App',
      installing: 'Installing...',
      benefits: 'Benefits of installing:',
      benefit1: 'Quick access from your home screen',
      benefit2: 'Full screen experience',
      benefit3: 'Works offline (partial)',
      benefit4: 'Push notifications',
      iosTitle: 'Install on iOS',
      iosStep1: 'Tap the Share button',
      iosStep2: 'Select "Add to Home Screen"',
      iosStep3: 'Tap "Add" to confirm',
      androidTitle: 'Install on Android',
      androidStep1: 'Tap the menu (3 dots)',
      androidStep2: 'Select "Install app"',
      androidStep3: 'Confirm installation',
      desktopTitle: 'Install on Desktop',
      desktopStep1: 'Click the install icon in the address bar',
      desktopStep2: 'Or use the button below',
      installed: 'App installed successfully!',
      installError: 'Could not install the app',
    }
  };

  const t = txt[language] || txt.es;

  // Track modal opening — fires once per false→true transition
  useEffect(() => {
    if (isOpen) {
      trackPwa('ios_install_modal_opened');
    }
  }, [isOpen]);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed (standalone mode)
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                               window.navigator.standalone === true;
    setIsStandalone(isInStandaloneMode);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      toast.success(t.installed);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [t.installed]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      toast.error(t.installError);
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setInstalled(true);
        toast.success(t.installed);
      }
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Install error:', error);
      toast.error(t.installError);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-6 bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600">
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
              <Download className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{t.title}</h2>
              <p className="text-white/80 text-sm">{t.subtitle}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Already Installed State */}
          {isStandalone || installed ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-zinc-700/40 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-zinc-300" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{t.alreadyInstalled}</h3>
              <p className="text-sm text-zinc-400">{t.alreadyInstalledDesc}</p>
            </div>
          ) : (
            <>
              {/* Benefits */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-zinc-300">{t.benefits}</h3>
                <ul className="space-y-2">
                  {[t.benefit1, t.benefit2, t.benefit3, t.benefit4].map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-400">
                      <Check className="w-4 h-4 text-teal-400 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              {/* iOS Instructions */}
              {isIOS && (
                <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                  <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-teal-400" />
                    {t.iosTitle}
                  </h4>
                  <ol className="space-y-2 text-sm text-zinc-400">
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs">1</span>
                      <span className="flex items-center gap-1">{t.iosStep1} <Share className="w-4 h-4 text-blue-400" /></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs">2</span>
                      {t.iosStep2}
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs">3</span>
                      {t.iosStep3}
                    </li>
                  </ol>
                </div>
              )}

              {/* Desktop/Android with prompt */}
              {!isIOS && deferredPrompt && (
                <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                  <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-teal-400" />
                    {t.desktopTitle}
                  </h4>
                  <p className="text-sm text-zinc-400 mb-3">{t.desktopStep2}</p>
                  <button
                    onClick={handleInstall}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white font-bold rounded-lg transition-all"
                    data-testid="install-app-btn"
                  >
                    <Download className="w-5 h-5" />
                    {t.installBtn}
                  </button>
                </div>
              )}

              {/* Android without prompt (instructions) */}
              {!isIOS && !deferredPrompt && (
                <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                  <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-teal-400" />
                    {t.androidTitle}
                  </h4>
                  <ol className="space-y-2 text-sm text-zinc-400">
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs">1</span>
                      <span className="flex items-center gap-1">{t.androidStep1} <MoreVertical className="w-4 h-4 text-zinc-500" /></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs">2</span>
                      {t.androidStep2}
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs">3</span>
                      {t.androidStep3}
                    </li>
                  </ol>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InstallAppModal;
